import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bar, BarChart, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { cartoesApi, orcamentosApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Input, Select, Toggle } from '../../components/ui/Input'
import { Card, CardBody, CardHeader } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { LoadingState, ErrorState } from '../../components/ui/EmptyState'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { TransactionType, TipoRecorrencia } from '../../lib/enums'
import { formatMoney, formatDate } from '../../lib/utils'
import { Dica } from './PlanejamentoPanel'
import type { CompraSimulada, EntradaSimulada, MesProjetado, SituacaoProjecao } from '../../types'

const MES_CURTO = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
const MES_LONGO = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']
const COR: Record<SituacaoProjecao, string> = { ok: '#FFB300', abaixoDaReserva: '#F59E0B', negativo: '#EF4444' }
const ORIGEM: Record<string, string> = {
  fatura: 'Faturas', orcamento: 'Orçamentos à vista', fixa: 'Fixas sem orçamento', financiamento: 'Financiamentos', conta: 'Contas lançadas', atrasada: 'Em aberto de antes',
}
const nomeMes = (m: { mes: number; ano: number }) => `${MES_LONGO[m.mes - 1]} de ${m.ano}`
const doMesInput = (v: string) => ({ ano: Number(v.slice(0, 4)), mes: Number(v.slice(5, 7)) })
const paraMesInput = (mes: number, ano: number) => `${ano}-${String(mes).padStart(2, '0')}`

/** Próximos meses: projeção de saldo, entradas simuladas e simulador de compra (docs/specs/orcamento-plano-caixa-projecao.md). */
export function ProximosMesesPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()
  const [meses, setMeses] = useState(6)
  // null = usa as entradas salvas no servidor; array = o conjunto que está na tela (salvo ou não)
  const [entradas, setEntradas] = useState<EntradaSimulada[] | null>(null)
  const salvasRef = useRef<string>('[]')
  const [mesAberto, setMesAberto] = useState(0)
  const [novaEntrada, setNovaEntrada] = useState(false)
  const [sairPara, setSairPara] = useState<{ to: string; state?: unknown } | null>(null)

  const { data: p, isLoading, isError } = useQuery({
    queryKey: ['projecao', meses, entradas],
    queryFn: () => orcamentosApi.projetar({ meses, entradas }),
    placeholderData: (anterior) => anterior,
  })
  useEffect(() => { if (p && entradas === null) salvasRef.current = JSON.stringify(p.entradasSimuladas) }, [p, entradas])

  const atuais = entradas ?? p?.entradasSimuladas ?? []
  const sujo = entradas !== null && JSON.stringify(entradas) !== salvasRef.current

  // "Salvar as simulações?" ao sair: fechar a aba e clicar em links do app (o BrowserRouter não tem bloqueio de navegação).
  useEffect(() => {
    if (!sujo) return
    const antesDeFechar = (e: BeforeUnloadEvent) => { e.preventDefault() }
    const aoClicar = (e: MouseEvent) => {
      const a = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null
      if (!a || a.target === '_blank' || e.ctrlKey || e.metaKey) return
      const url = new URL(a.href)
      if (url.origin !== location.origin || url.pathname === location.pathname) return
      e.preventDefault()
      e.stopPropagation()
      setSairPara({ to: url.pathname + url.search })
    }
    window.addEventListener('beforeunload', antesDeFechar)
    document.addEventListener('click', aoClicar, true)
    return () => { window.removeEventListener('beforeunload', antesDeFechar); document.removeEventListener('click', aoClicar, true) }
  }, [sujo])

  const salvar = useMutation({
    mutationFn: () => orcamentosApi.salvarSimulacoes(atuais),
    onSuccess: () => { salvasRef.current = JSON.stringify(atuais); setEntradas(null); qc.invalidateQueries({ queryKey: ['projecao'] }); toast.success('Simulações salvas.') },
    onError: (e: Error) => toast.error(e.message),
  })
  const limpar = useMutation({
    mutationFn: () => orcamentosApi.limparSimulacoes(),
    onSuccess: () => { salvasRef.current = '[]'; setEntradas(null); qc.invalidateQueries({ queryKey: ['projecao'] }); toast.success('Simulações apagadas.') },
    onError: (e: Error) => toast.error(e.message),
  })
  const conta = useMutation({
    mutationFn: ({ id, entra }: { id: string; entra: boolean }) => orcamentosApi.contaNaProjecao(id, entra),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['projecao'] }); qc.invalidateQueries({ queryKey: ['orcamento'] }) },
    onError: (e: Error) => toast.error(e.message),
  })

  function sair(to: string, state?: unknown) {
    if (sujo) setSairPara({ to, state })
    else navigate(to, { state })
  }

  function virarRecorrente(e: EntradaSimulada) {
    sair('/recorrencias', {
      novaRecorrencia: {
        description: e.descricao, amount: String(e.valor), type: TransactionType.Income, tipoRecorrencia: TipoRecorrencia.Mensal,
        dataInicio: `${paraMesInput(e.mes, e.ano)}-01`,
      },
    })
  }

  if (isLoading && !p) return <><PageHeader title="Próximos meses" /><LoadingState /></>
  if (isError || !p) return <><PageHeader title="Próximos meses" /><ErrorState message="Não consegui calcular a projeção." /></>

  const mesesVisiveis = p.meses.slice(0, meses)
  const detalhe = mesesVisiveis[Math.min(mesAberto, mesesVisiveis.length - 1)]
  const reserva = p.reserva.valorCalculado
  const abaixo = mesesVisiveis.filter((m) => m.situacao !== 'ok')

  return (
    <>
      <PageHeader title="Próximos meses" />
      <div className="mb-4">
        <button type="button" onClick={() => sair('/orcamento')} className="text-13 text-primary hover:underline"><i className="ri-arrow-left-line" aria-hidden /> Voltar ao Orçamento</button>
      </div>

      <div className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="border border-primary/60 lg:col-span-1">
            <CardBody>
              <p className="text-13 text-muted">Posso gastar ou guardar hoje</p>
              <p className="mt-1 text-3xl font-bold text-white">{formatMoney(p.podeGastarOuGuardarHoje)}</p>
              <p className="mt-1 text-2xs text-muted">
                {reserva > 0 ? `Sem nenhum mês ficar abaixo da reserva de ${formatMoney(reserva)}.` : 'Sem nenhum mês ficar negativo.'}
              </p>
              {p.quandoTereiDinheiro && <p className="mt-2 text-13 text-info">Você volta a ter folga a partir de {nomeMes(p.quandoTereiDinheiro)}.</p>}
              <p className="mt-3 text-2xs text-muted">Saldo de hoje nas contas marcadas: {formatMoney(p.saldoInicial)}. Renda prevista: só as recorrências cadastradas e as entradas simuladas.</p>
            </CardBody>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader title="Saldo no fim de cada mês" icon="ri-bar-chart-2-line"
              actions={<Button size="sm" variant="light" onClick={() => { setMeses(meses === 6 ? 12 : 6); setMesAberto(0) }}>{meses === 6 ? 'Ver 12 meses' : 'Ver 6 meses'}</Button>} />
            <CardBody>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={mesesVisiveis.map((m) => ({ nome: MES_CURTO[m.mes - 1], saldo: m.saldoFim, situacao: m.situacao }))}
                    onClick={(e) => { if (e && typeof e.activeTooltipIndex === 'number') setMesAberto(e.activeTooltipIndex) }}>
                    <XAxis dataKey="nome" stroke="#8B949E" fontSize={12} />
                    <YAxis stroke="#8B949E" fontSize={11} tickFormatter={(v) => `${Math.round(Number(v) / 1000)}k`} width={36} />
                    <Tooltip formatter={(v) => formatMoney(Number(v))} contentStyle={{ background: '#1B2330', border: '1px solid #2D3748' }} cursor={{ fill: '#ffffff10' }} />
                    {reserva > 0 && <ReferenceLine y={reserva} stroke="#F59E0B" strokeDasharray="4 4" label={{ value: `reserva ${formatMoney(reserva)}`, fill: '#F59E0B', fontSize: 11, position: 'insideTopRight' }} />}
                    <ReferenceLine y={0} stroke="#2D3748" />
                    <Bar dataKey="saldo" radius={[4, 4, 0, 0]} cursor="pointer">
                      {mesesVisiveis.map((m, i) => (
                        <Cell key={i} fill={COR[m.situacao]} stroke={COR[m.situacao]} strokeWidth={m.situacao === 'abaixoDaReserva' ? 1.5 : 0}
                          fillOpacity={(m.situacao === 'abaixoDaReserva' ? 0.3 : 0.75) + (i === mesAberto ? 0.25 : 0)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {abaixo.length > 0 && (
                <p className="mt-2 text-13 text-warning">
                  {abaixo.map((m) => `${MES_LONGO[m.mes - 1]} ${m.situacao === 'negativo' ? 'fica negativo' : 'fica abaixo da reserva'} (${formatMoney(m.saldoFim)})`).join('; ')}
                </p>
              )}
              {p.avisos.map((a) => <p key={a} className="mt-1 text-2xs text-warning"><i className="ri-error-warning-line" aria-hidden /> {a}</p>)}
            </CardBody>
          </Card>
        </div>

        {detalhe && <DetalheDoMes m={detalhe} />}

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader title={<>Entradas simuladas<Dica texto="Dinheiro que você espera receber e não está cadastrado (um freela, um 13º). Entra só aqui na projeção: não muda o saldo, os relatórios nem o Orçamento." /></>} icon="ri-flask-line"
              actions={<Button size="sm" icon="ri-add-line" onClick={() => setNovaEntrada(true)}>Adicionar</Button>} />
            <CardBody className="space-y-3">
              {atuais.length === 0 ? <p className="text-13 text-muted">Nenhuma entrada simulada.</p> : (
                <ul className="divide-y divide-border/40">
                  {atuais.map((e, i) => (
                    <li key={`${e.descricao}-${i}`} className="flex flex-wrap items-center justify-between gap-2 py-2">
                      <div className="min-w-0">
                        <p className="text-13 text-white">{e.descricao} <Badge variant="info">simulada</Badge></p>
                        <p className="text-2xs text-muted">
                          {formatMoney(e.valor)} · {e.ateMes && e.ateAno ? `todo mês de ${MES_CURTO[e.mes - 1]}/${e.ano} a ${MES_CURTO[e.ateMes - 1]}/${e.ateAno}` : `em ${MES_CURTO[e.mes - 1]}/${e.ano}`}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="light" icon="ri-repeat-line" onClick={() => virarRecorrente(e)}>Virar recorrente</Button>
                        <Button size="sm" variant="light" icon="ri-close-line" onClick={() => setEntradas(atuais.filter((_, j) => j !== i))}>Tirar</Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-2">
                {sujo && <Button size="sm" icon="ri-save-line" loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar simulações</Button>}
                {(atuais.length > 0 || salvasRef.current !== '[]') && <Button size="sm" variant="soft-danger" icon="ri-delete-bin-line" loading={limpar.isPending} onClick={() => limpar.mutate()}>Limpar simulações</Button>}
              </div>
              {sujo && <p className="text-2xs text-warning">Há simulações não salvas. Ao sair da tela, o app pergunta se você quer salvar.</p>}
              {atuais.length > 0 && <p className="text-2xs text-muted">Depois de virar recorrente, tire a simulação para o valor não contar duas vezes.</p>}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title={<>Contas na projeção<Dica texto="A projeção parte do saldo de hoje das contas marcadas. Poupança, investimento e vale-alimentação começam desmarcados." /></>} icon="ri-bank-line" />
            <CardBody className="space-y-2">
              {p.contas.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3">
                  <Toggle label={c.nome} checked={c.entraNaProjecao} onChange={(v) => conta.mutate({ id: c.id, entra: v })} />
                  <span className="text-13 text-muted">{formatMoney(c.saldo)}</span>
                </div>
              ))}
              <ReservaMinima atual={p.reserva} />
            </CardBody>
          </Card>
        </div>

        <Simulador meses={meses} entradas={entradas} contas={p.contas} />
      </div>

      <NovaEntradaModal open={novaEntrada} onClose={() => setNovaEntrada(false)} onAdicionar={(e) => { setEntradas([...atuais, e]); setNovaEntrada(false) }} />

      <Modal open={!!sairPara} onClose={() => setSairPara(null)} title="Salvar as simulações?" size="sm"
        footer={<>
          <Button variant="light" loading={salvar.isPending} onClick={async () => { const s = sairPara!; await salvar.mutateAsync(); setSairPara(null); navigate(s.to, { state: s.state }) }}>Salvar</Button>
          <Button onClick={() => { const s = sairPara!; setSairPara(null); setEntradas(null); navigate(s.to, { state: s.state }) }} autoFocus>Não salvar</Button>
        </>}>
        <p className="text-13 text-muted">Você tem entradas simuladas que ainda não foram salvas. Salvas, elas continuam na projeção da próxima vez (no Web e no celular).</p>
      </Modal>
    </>
  )
}

function DetalheDoMes({ m }: { m: MesProjetado }) {
  const grupos = Object.entries(m.saidas.reduce<Record<string, MesProjetado['saidas']>>((acc, s) => ({ ...acc, [s.origem]: [...(acc[s.origem] ?? []), s] }), {}))
  return (
    <Card>
      <CardHeader title={`${MES_LONGO[m.mes - 1][0].toUpperCase()}${MES_LONGO[m.mes - 1].slice(1)} de ${m.ano}`} icon="ri-calendar-line" />
      <CardBody className="space-y-2 text-13">
        <Linha l="Saldo no início" v={m.saldoInicio} />
        <details>
          <summary className="flex cursor-pointer justify-between text-income"><span>+ Entradas previstas</span><span>{formatMoney(m.renda)}</span></summary>
          <ul className="mt-1 space-y-1 pl-4">
            {m.entradas.map((e, i) => <li key={i} className="flex justify-between text-muted"><span>{e.descricao}{e.origem === 'simulada' ? ' (simulada)' : ''}</span><span>{formatMoney(e.valor)}</span></li>)}
          </ul>
        </details>
        {grupos.map(([origem, itens]) => (
          <details key={origem}>
            <summary className="flex cursor-pointer justify-between text-white"><span>− {ORIGEM[origem] ?? origem}</span><span>{formatMoney(itens.reduce((t, s) => t + s.valor, 0))}</span></summary>
            <ul className="mt-1 space-y-1 pl-4">
              {itens.map((s, i) => <li key={i} className="flex justify-between text-muted"><span>{s.descricao}</span><span>{formatMoney(s.valor)}</span></li>)}
            </ul>
          </details>
        ))}
        <div className="border-t border-border/60 pt-2">
          <Linha l="= Saldo no fim" v={m.saldoFim} forte cor={COR[m.situacao]} />
        </div>
      </CardBody>
    </Card>
  )
}

function Linha({ l, v, forte, cor }: { l: string; v: number; forte?: boolean; cor?: string }) {
  return <div className={`flex justify-between ${forte ? 'font-semibold' : ''}`}><span className="text-white">{l}</span><span style={cor ? { color: cor } : undefined} className={cor ? '' : 'text-white'}>{formatMoney(v)}</span></div>
}

function ReservaMinima({ atual }: { atual: { tipo: 'valor' | 'fixas' | null; valor?: number | null; valorCalculado: number } }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [tipo, setTipo] = useState<'' | 'valor' | 'fixas'>(atual.tipo ?? '')
  const [valor, setValor] = useState(atual.valor ? String(atual.valor) : '')
  const salvar = useMutation({
    mutationFn: () => orcamentosApi.reserva(tipo || null, tipo === 'valor' ? Number(valor) : null),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['projecao'] }); toast.success('Reserva mínima salva.') },
    onError: (e: Error) => toast.error(e.message),
  })
  const mudou = (tipo || null) !== atual.tipo || (tipo === 'valor' && Number(valor) !== (atual.valor ?? 0))
  return (
    <div className="mt-3 space-y-2 border-t border-border/60 pt-3">
      <p className="flex items-center text-13 font-medium text-white">Reserva mínima<Dica texto="Opcional. A projeção e o simulador avisam quando algum mês fica abaixo desse valor." /></p>
      <div className="grid gap-2 sm:grid-cols-2">
        <Select value={tipo} onChange={(e) => setTipo(e.target.value as typeof tipo)}>
          <option value="">Desligada</option>
          <option value="valor">Um valor fixo</option>
          <option value="fixas">1 mês de contas fixas</option>
        </Select>
        {tipo === 'valor' && <Input type="number" min={0} step="0.01" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="500,00" />}
      </div>
      {tipo === 'fixas' && atual.tipo === 'fixas' && <p className="text-2xs text-muted">Hoje: {formatMoney(atual.valorCalculado)} (contas fixas do próximo mês).</p>}
      {mudou && <Button size="sm" loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar reserva</Button>}
    </div>
  )
}

function NovaEntradaModal({ open, onClose, onAdicionar }: { open: boolean; onClose: () => void; onAdicionar: (e: EntradaSimulada) => void }) {
  const hoje = new Date()
  const [f, setF] = useState({ descricao: '', valor: '', mes: paraMesInput(hoje.getMonth() + 1, hoje.getFullYear()), repete: false, ate: '' })
  const [erro, setErro] = useState<string | null>(null)
  function adicionar() {
    if (!f.descricao.trim()) return setErro('Dê um nome para a entrada.')
    if (!(Number(f.valor) > 0)) return setErro('Informe um valor maior que zero.')
    if (f.repete && (!f.ate || f.ate < f.mes)) return setErro('Escolha até quando repete (depois do primeiro mês).')
    const de = doMesInput(f.mes)
    const ate = f.repete ? doMesInput(f.ate) : null
    onAdicionar({ id: null, descricao: f.descricao.trim(), valor: Number(f.valor), mes: de.mes, ano: de.ano, ateMes: ate?.mes ?? null, ateAno: ate?.ano ?? null })
    setF({ ...f, descricao: '', valor: '' })
    setErro(null)
  }
  return (
    <Modal open={open} onClose={onClose} title="Adicionar entrada simulada" size="sm"
      footer={<><Button variant="light" onClick={onClose}>Cancelar</Button><Button onClick={adicionar}>Adicionar</Button></>}>
      <div className="space-y-3">
        <Input label="Descrição" value={f.descricao} onChange={(e) => { setF({ ...f, descricao: e.target.value }); setErro(null) }} placeholder="Freela de dezembro" />
        <Input label="Valor (R$)" type="number" min={0} step="0.01" value={f.valor} onChange={(e) => { setF({ ...f, valor: e.target.value }); setErro(null) }} />
        <Input label="Mês" type="month" value={f.mes} onChange={(e) => setF({ ...f, mes: e.target.value })} />
        <Toggle label="Repete todo mês" checked={f.repete} onChange={(v) => setF({ ...f, repete: v })} />
        {f.repete && <Input label="Até" type="month" value={f.ate} onChange={(e) => { setF({ ...f, ate: e.target.value }); setErro(null) }} />}
        {erro && <p className="text-13 text-expense">{erro}</p>}
      </div>
    </Modal>
  )
}

function Simulador({ meses, entradas, contas }: { meses: number; entradas: EntradaSimulada[] | null; contas: { id: string; nome: string }[] }) {
  const toast = useToast()
  const { data: cartoes = [] } = useQuery({ queryKey: ['cartoes'], queryFn: cartoesApi.list })
  const credito = cartoes.filter((c) => c.modalidade === 1)
  const [f, setF] = useState({ valor: '', parcelas: '1', forma: 'credito' as 'credito' | 'avista', data: '', cartaoId: '', contaId: '' })
  const simular = useMutation({
    mutationFn: () => {
      const compra: CompraSimulada = {
        valor: Number(f.valor), parcelas: Number(f.parcelas), forma: f.forma, data: f.data || null,
        cartaoId: f.forma === 'credito' ? f.cartaoId || null : null, contaId: f.forma === 'avista' ? f.contaId || null : null,
      }
      return orcamentosApi.projetar({ meses, entradas, compra })
    },
    onError: (e: Error) => toast.error(e.message),
  })
  const r = simular.data
  const sim = r?.simulacao

  return (
    <Card>
      <CardHeader title={<>Simular uma compra<Dica texto="Mostra em qual fatura cai cada parcela e como ficam os próximos meses. Nada é gravado." /></>} icon="ri-shopping-bag-3-line" />
      <CardBody className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input label="Valor (R$)" type="number" min={0} step="0.01" value={f.valor} onChange={(e) => setF({ ...f, valor: e.target.value })} placeholder="1.200,00" />
          <div>
            <span className="form-label">Forma</span>
            <div className="grid grid-cols-2 gap-2">
              {(['credito', 'avista'] as const).map((x) => (
                <button key={x} type="button" aria-pressed={f.forma === x} onClick={() => setF({ ...f, forma: x })}
                  className={`h-[38px] rounded border text-13 ${f.forma === x ? 'border-primary bg-primary/10 text-primary' : 'border-border/70 text-muted hover:text-white'}`}>
                  {x === 'credito' ? 'Crédito' : 'À vista'}
                </button>
              ))}
            </div>
          </div>
          <Input label="Parcelas" type="number" min={1} max={48} step={1} value={f.parcelas} onChange={(e) => setF({ ...f, parcelas: e.target.value })} />
          <Input label="Data (padrão: hoje)" type="date" value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} />
          {f.forma === 'credito' ? (
            <Select label="Cartão" value={f.cartaoId} onChange={(e) => setF({ ...f, cartaoId: e.target.value })}>
              <option value="">O mais usado</option>
              {credito.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </Select>
          ) : (
            <Select label="Conta" value={f.contaId} onChange={(e) => setF({ ...f, contaId: e.target.value })}>
              <option value="">A conta principal</option>
              {contas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </Select>
          )}
        </div>
        <Button icon="ri-calculator-line" loading={simular.isPending} disabled={!(Number(f.valor) > 0) || !(Number(f.parcelas) >= 1)} onClick={() => simular.mutate()}>Simular</Button>

        {sim && r && (
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-3">
              <div className={`rounded border p-3 ${sim.veredito === 'cabe' ? 'border-income/60' : sim.veredito === 'negativo' ? 'border-expense/60' : 'border-warning/60'}`}>
                <p className={`font-semibold ${sim.veredito === 'cabe' ? 'text-income' : sim.veredito === 'negativo' ? 'text-expense' : 'text-warning'}`}>
                  {sim.veredito === 'cabe' ? 'Cabe' : sim.veredito === 'negativo' ? `Fica negativo em ${sim.mesProblema ? nomeMes(sim.mesProblema) : ''}` : `Fica abaixo da reserva em ${sim.mesProblema ? nomeMes(sim.mesProblema) : ''}`}
                </p>
                {sim.veredito !== 'cabe' && <p className="text-13 text-muted">Faltam {formatMoney(sim.falta)}.</p>}
                {sim.quandoComprar && <p className="mt-1 text-13 text-info">Comprando a partir de {formatDate(sim.quandoComprar)}, a compra cabe.</p>}
                {sim.veredito !== 'cabe' && !sim.quandoComprar && <p className="mt-1 text-13 text-muted">Nos próximos {meses} meses não há data em que essa compra caiba.</p>}
                {sim.aviso && <p className="mt-1 text-2xs text-warning">{sim.aviso}</p>}
              </div>
              <ul className="divide-y divide-border/40 text-13">
                {sim.parcelas.map((x) => (
                  <li key={x.numero} className="flex justify-between py-1.5">
                    <span className="text-white">{x.numero}ª · {f.forma === 'credito' ? `fatura que vence ${formatDate(x.vencimento)}` : formatDate(x.vencimento)} <span className="text-muted">({x.fonte})</span></span>
                    <span className="text-white">{formatMoney(x.valor)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={r.meses.map((m, i) => ({ nome: `${MES_CURTO[m.mes - 1]}/${String(m.ano).slice(2)}`, antes: m.saldoFim, depois: sim.depois[i]?.saldoFim }))}>
                  <XAxis dataKey="nome" stroke="#8B949E" fontSize={11} />
                  <YAxis stroke="#8B949E" fontSize={11} tickFormatter={(v) => `${Math.round(Number(v) / 1000)}k`} width={36} />
                  <Tooltip formatter={(v) => formatMoney(Number(v))} contentStyle={{ background: '#1B2330', border: '1px solid #2D3748' }} cursor={{ fill: '#ffffff10' }} />
                  {r.reserva.valorCalculado > 0 && <ReferenceLine y={r.reserva.valorCalculado} stroke="#F59E0B" strokeDasharray="4 4" />}
                  <ReferenceLine y={0} stroke="#2D3748" />
                  <Bar dataKey="antes" name="Antes" fill="#64748B" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="depois" name="Depois" fill="#FFB300" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <p className="mt-1 text-center text-2xs text-muted"><span className="text-[#64748B]">■</span> antes · <span className="text-primary">■</span> depois · só simula, nada é gravado</p>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

